import { useEffect, useState } from "react";
import { Card, Button } from "react-bootstrap";
import ResizableSplitView from "@components/ResizableSplitView/ResizableSplitView";
import Loader from "src/components/common/loader/loader";
import { useDocumentTitle } from "@/shared/hooks/useDocumentTitle";
import { NetworkTemplateService } from "@services/contentNetwork/networkTemplates/networkTemplateService";
import type { NetworkTemplate } from "@models/networkTemplate.model";
import { NetworkTemplateList } from "./components/NetworkTemplateList";
import { NetworkTemplateDetail } from "./components/NetworkTemplateDetail";
import { ImportNetworkTemplateModal } from "./components/ImportNetworkTemplateModal";

export const NetworkTemplatesPage = () => {
  useDocumentTitle("Network Templates");

  const [templates, setTemplates] = useState<NetworkTemplate[]>([]);
  const [selected, setSelected] = useState<NetworkTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [importOpen, setImportOpen] = useState(false);

  useEffect(() => {
    const service = new NetworkTemplateService();
    service
      .list()
      .then(setTemplates)
      .catch((err) => console.error("Failed to load network templates:", err))
      .finally(() => setLoading(false));
  }, []);

  const handleImported = (template: NetworkTemplate) => {
    setTemplates((prev) => [...prev, template]);
    setSelected(template);
  };

  const handleUpdated = (template: NetworkTemplate) => {
    setTemplates((prev) => prev.map((t) => (t.id === template.id ? template : t)));
    setSelected(template);
  };

  const handleDeleted = (templateId: number) => {
    setTemplates((prev) => prev.filter((t) => t.id !== templateId));
    setSelected((current) => (current?.id === templateId ? null : current));
  };

  const handleSelect = async (template: NetworkTemplate) => {
    setSelected(template);
    // The list endpoint already returns full `data`, but refresh the detail to
    // stay aligned with the single-template endpoint.
    try {
      const detail = await new NetworkTemplateService().getDetail(template.id);
      setSelected(detail);
    } catch (err) {
      console.error("Failed to load template detail:", err);
    }
  };

  return (
    <div className="p-3 h-100">
      <Card className="custom-card h-100 d-flex flex-column">
        <Card.Header className="flex-shrink-0 d-flex justify-content-between align-items-center">
          <Card.Title className="mb-0">Network Templates</Card.Title>
          <Button
            size="sm"
            variant="primary"
            onClick={() => setImportOpen(true)}
            className="d-flex align-items-center gap-1"
          >
            <i className="bi bi-box-arrow-in-down"></i>
            Import Template
          </Button>
        </Card.Header>
        <Card.Body className="p-0 flex-grow-1" style={{ minHeight: 0, overflow: "hidden" }}>
          {loading ? (
            <Loader />
          ) : (
            <ResizableSplitView
              left={
                <NetworkTemplateList
                  templates={templates}
                  selectedId={selected?.id ?? null}
                  onSelect={handleSelect}
                />
              }
              right={
                <NetworkTemplateDetail
                  template={selected}
                  onUpdated={handleUpdated}
                  onDeleted={handleDeleted}
                />
              }
              defaultLeftSize={250}
              minLeftSize={200}
              minRightSize={400}
              storageKey="network-templates-split"
            />
          )}
        </Card.Body>
      </Card>

      {importOpen && (
        <ImportNetworkTemplateModal
          onClose={() => setImportOpen(false)}
          onImported={handleImported}
        />
      )}
    </div>
  );
};
